'use client';

import {
  type CSSProperties,
  type ReactNode,
  type RefObject,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';

import { SpinnerIcon } from '../icons/SpinnerIcon';
import { cn } from '../utils';
import { RailScrollButtons, useRailScroll } from './RailScroll';

export type InfiniteAppendOrientation = 'vertical' | 'horizontal';

/**
 * Loads one more page server-side and returns its items as an RSC payload plus
 * whether further pages remain. Typically a bound server action, so the client
 * passes only the page number and the appended content is the same server
 * render, never client-shaped.
 */
export type InfiniteAppendLoadMore = (
  page: number,
) => Promise<{ node: ReactNode; hasMore: boolean }>;

interface InfiniteAppendListProps {
  /** The server-rendered first page (items, not the container). */
  children?: ReactNode;
  /** Page number rendered as `children` (usually 1; higher on a `?page=N` cold load). */
  initialPage: number;
  /** Whether pages exist beyond `children`. */
  initialHasMore: boolean;
  /** Loads and returns the next page as an RSC payload. See {@link InfiniteAppendLoadMore}. */
  loadMore: InfiniteAppendLoadMore;
  /**
   * `vertical` (default) appends downward and observes the viewport; `horizontal`
   * appends rightward inside its own scroll container (a rail) and shows the
   * loading indicator as a trailing cell.
   */
  orientation?: InfiniteAppendOrientation;
  /** Classes for the list/scroll container (`flex-col`, a rail's `overflow-x-auto`, …). */
  className?: string;
  /** Inline style for the container — for creator-themed surfaces / scroll-snap. */
  style?: CSSProperties;
  /**
   * Crawlable base URL (WITHOUT a page param) for the vertical load-more anchor.
   * Present → the trailing indicator is wrapped in a real `<a href="…?page=N">`
   * (SEO + no-JS reachable) whose click is intercepted to append in place. Absent
   * → a plain spinner that auto-loads on scroll (horizontal rails, or any list
   * whose items are already crawlable via their own detail links).
   */
  basePath?: string;
  /** Accessible label for the load-more anchor (default "Load more"). */
  loadMoreLabel?: string;
  /** Classes merged onto the trailing indicator wrapper. */
  indicatorClassName?: string;
  /** Inline style for the trailing indicator wrapper — e.g. a rail cell's width. */
  indicatorStyle?: CSSProperties;
  /**
   * How far before the scroll edge to start loading, as a fraction of the ROOT's
   * size — the viewport height for `vertical`, the rail's own width for
   * `horizontal`. `0.5` (default) starts when the sentinel is within half a
   * viewport / half a rail of the edge. Responsive to the surface, unlike a fixed
   * px margin (a narrow rail and a tall page trigger at the same relative point).
   */
  preloadRatio?: number;
  /**
   * `horizontal` only: overlay prev/next scroll arrows on the rail's edges (they
   * auto-hide when the rail doesn't overflow, and each hides at its end). Opt-in
   * so non-rail horizontal uses and every vertical list stay unchanged. Wraps the
   * scroll container in a `relative` div so the arrows can anchor to it.
   */
  showScrollArrows?: boolean;
  /** Accessible label for the left arrow when `showScrollArrows` (default "Previous"). */
  scrollPrevLabel?: string;
  /** Accessible label for the right arrow when `showScrollArrows` (default "Next"). */
  scrollNextLabel?: string;
  /**
   * `horizontal` only: the caller's ref for the scroll container, so it can drive
   * the rail from its OWN chevrons (e.g. drawn inline in a section header rather
   * than overlaid by `showScrollArrows`). Pair it with the shared
   * {@link useRailScroll}. Omitted → the list keeps its private ref.
   */
  scrollRef?: RefObject<HTMLDivElement | null>;
}

/** Append `?page=N` (or `&page=N` when the base already carries a query). */
function appendPageParam(base: string, page: number): string {
  const separator = base.includes('?') ? '&' : '?';
  return `${base}${separator}page=${page}`;
}

/**
 * The vertical load-more pill — consistent across the bio feed/tab/detail
 * surfaces. Uses `currentColor` (not white) so it inherits the surface's
 * contrasting text color — readable on a dark course detail AND a light-themed
 * bio tab, where the old white pill was invisible.
 */
const LOAD_MORE_PILL_CLASS =
  'rounded-full border border-current px-5 py-2 text-[13px] font-medium opacity-70 transition hover:opacity-100';

/**
 * Progressive-enhancement infinite scroll over a server-rendered first page,
 * generic across orientation. The first page arrives as server-rendered
 * `children`; as a trailing sentinel nears the scroll edge, each subsequent page
 * is fetched through `loadMore` (a bound server action returning MORE
 * server-rendered items) and appended in place — so lazily-loaded content is the
 * same server render, never client-shaped.
 *
 * - `vertical`: observes the viewport; the indicator sits below the list. With a
 *   `basePath`, it's a crawlable `<a href="?page=N">` a search engine follows;
 *   JS intercepts it to append seamlessly.
 * - `horizontal`: observes the rail's own scroll container; the indicator is a
 *   trailing cell (a spinner in the next slot, matching the upstream rail).
 */
export function InfiniteAppendList({
  children,
  initialPage,
  initialHasMore,
  loadMore,
  orientation = 'vertical',
  className,
  style,
  basePath,
  loadMoreLabel = 'Load more',
  indicatorClassName,
  indicatorStyle,
  preloadRatio = 0.5,
  showScrollArrows = false,
  scrollPrevLabel = 'Previous',
  scrollNextLabel = 'Next',
  scrollRef: callerScrollRef,
}: InfiniteAppendListProps) {
  const [batches, setBatches] = useState<ReactNode[]>([]);
  const [page, setPage] = useState(initialPage);
  const [hasMore, setHasMore] = useState(initialHasMore);
  const [pending, startTransition] = useTransition();
  const loadingRef = useRef(false);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const ownScrollRef = useRef<HTMLDivElement | null>(null);
  const scrollRef = callerScrollRef ?? ownScrollRef;
  const isHorizontal = orientation === 'horizontal';

  // Prev/next arrows for a horizontal rail (opt-in). Re-measures as pages append
  // (`page` grows) so the right arrow reappears once more clips load in.
  const { scrollByDirection, ...railScroll } = useRailScroll(scrollRef, page);

  const loadNext = () => {
    if (loadingRef.current || !hasMore) {
      return;
    }
    loadingRef.current = true;
    // `loadMore` is a Server Action; invoking it re-renders the route's Server
    // Components. Run it inside a transition so React keeps the ALREADY-REVEALED
    // content during that re-render instead of swapping in the nearest
    // `<Suspense>` fallback — otherwise, when the (uncached) detail loader
    // re-awaits, the list is blanked by its skeleton and scroll jumps to the top.
    // In a transition the list only ever grows.
    startTransition(async () => {
      try {
        const nextPage = page + 1;
        const result = await loadMore(nextPage);
        // State updates after an `await` inside a transition must re-enter one.
        startTransition(() => {
          setBatches(previous => [...previous, result.node]);
          setPage(nextPage);
          setHasMore(result.hasMore);
        });
      } catch (error) {
        console.error('InfiniteAppendList: failed to load more:', error);
      } finally {
        loadingRef.current = false;
      }
    });
  };

  // Keep the observer (subscribed once per hasMore state) pointed at the freshest
  // handler so it reads the current page without churning a subscription.
  const loadNextRef = useRef(loadNext);
  loadNextRef.current = loadNext;

  useEffect(() => {
    const target = sentinelRef.current;
    if (!target) {
      return;
    }
    // Preload threshold as a % of the ROOT's size (viewport for vertical, the
    // rail for horizontal) — responsive to the surface, unlike fixed px. Extends
    // the scroll edge the list grows toward: bottom (vertical) / right (horizontal).
    const marginPercent = `${Math.round(preloadRatio * 100)}%`;
    const observer = new IntersectionObserver(
      entries => {
        if (entries[0]?.isIntersecting) {
          void loadNextRef.current();
        }
      },
      {
        root: isHorizontal ? scrollRef.current : null,
        rootMargin: isHorizontal
          ? `0px ${marginPercent} 0px 0px`
          : `0px 0px ${marginPercent} 0px`,
      },
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [isHorizontal, hasMore, preloadRatio]);

  const nextHref = basePath ? appendPageParam(basePath, page + 1) : undefined;
  const label = pending ? 'Loading…' : loadMoreLabel;

  let indicator: ReactNode = null;
  if (hasMore && isHorizontal) {
    // A trailing cell the caller sizes + backgrounds to match its real cells (so
    // it reads as "the next cell, loading"); the spinner uses `currentColor`, set
    // by the caller's indicator text color.
    indicator = (
      <div
        ref={sentinelRef}
        className={cn(
          'flex shrink-0 items-center justify-center',
          indicatorClassName,
        )}
        style={indicatorStyle}
      >
        <SpinnerIcon className="h-6 w-6 animate-spin" />
      </div>
    );
  } else if (hasMore) {
    // Vertical: a "Load more" pill below the list — consistent across every bio
    // feed/tab/detail surface. When crawlable (`basePath`), it's a real
    // `<a href="?page=N">` a search engine follows; JS intercepts it to append.
    indicator = (
      <div ref={sentinelRef} className="flex w-full justify-center py-6">
        {nextHref ? (
          <a
            href={nextHref}
            onClick={event => {
              event.preventDefault();
              void loadNext();
            }}
            className={cn(LOAD_MORE_PILL_CLASS, indicatorClassName)}
          >
            {label}
          </a>
        ) : (
          <button
            type="button"
            onClick={() => void loadNext()}
            className={cn(LOAD_MORE_PILL_CLASS, indicatorClassName)}
          >
            {label}
          </button>
        )}
      </div>
    );
  }

  if (isHorizontal) {
    const rail = (
      <div ref={scrollRef} className={className} style={style}>
        {children}
        {batches}
        {indicator}
      </div>
    );
    // Wrap in a `relative` host so the overlaid arrows anchor to the rail. Only
    // when opted in — otherwise the bare scroll div renders exactly as before.
    return showScrollArrows ? (
      <div className="relative">
        {rail}
        <RailScrollButtons
          scroll={railScroll}
          onScroll={scrollByDirection}
          prevLabel={scrollPrevLabel}
          nextLabel={scrollNextLabel}
        />
      </div>
    ) : (
      rail
    );
  }

  return (
    <>
      <div className={className} style={style}>
        {children}
        {batches}
      </div>
      {indicator}
    </>
  );
}
